import React, { useEffect, useRef, useState } from "react";
import {Box, Button, Typography, MenuItem, Slider} from "@mui/material";
import io from "socket.io-client";
import { useAuthContext } from "../hooks/useAuthContext";
import SoundLevelGauge from "./SoundLevelGauge";
import VibrationGauge from "./VibrationGauge";
import {
  DMIC_TYPE, DMIC_GAIN_OPTIONS, ADC_VOL_MIN, ADC_VOL_MAX, ADC_VOL_UNITY,
  adcVolDb, dmicTotalGainDb, clipPeakG, formatG, formatDb
} from "../utils/dmicGain";
import TextField from "@mui/material/TextField"
import { set } from "react-hook-form";

function SoundLevelGaugeContainer({ clientId }) {
  const { user } = useAuthContext();
  const socketRef = useRef(null); // 用於存儲 socket 實例
  const [tiSplValue, settiSplValue] = useState(0); // 用於存儲 SPL 數據
  const [rtkSplValue, setRtkSplValue] = useState(0); // 用於存儲 SPL 數據
  const [tiGainValue, setTiGainValue] = useState(0); // 用於存儲增益數據
  const [rtkGainValue, setRtkGainValue] = useState(0); // 用於存儲增益數據
  const [rtkVolumeValue, setRtkVolumeValue] = useState(0); // 用於存儲增益數據
  const [tiVolumeValue, setTiVolumeValue] = useState(0); // 用於存儲增益數據
  // Realtek on a DMIC build is a vibration sensor: no SPL, but an unweighted g RMS value ("vpu")
  const [rtkIsDmic, setRtkIsDmic] = useState(false);
  const [vpuValue, setVpuValue] = useState(0);
  // Gain the device is running with (from config or the last set), used for the vibration scale
  const [appliedDmicTotalGain, setAppliedDmicTotalGain] = useState(0);
  // Apply sends all four values, so wait until they are loaded from the device config
  const [gainLoaded, setGainLoaded] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    setRtkIsDmic(false);
    setVpuValue(0);
    setGainLoaded(false);

    const fetchMicType = async () => {
      try {
        const response = await fetch(`/api/v1/device/mic-info/${clientId}`, {
          headers: { 'Authorization': `Bearer ${user.token}` }
        });
        if (!response.ok) return;
        const data = await response.json();
        if (isCancelled) return;
        const isDmic = data?.realtek_type === DMIC_TYPE;
        setRtkIsDmic(isDmic);
        // On DMIC devices realtek_gain / realtek_volume drive dmic_gain / adc_vol
        if (isDmic) {
          const dmicGain = data?.dmic_gain ?? 0;
          const adcVol = data?.adc_vol ?? ADC_VOL_UNITY;
          setRtkGainValue(dmicGain);
          setRtkVolumeValue(adcVol);
          setAppliedDmicTotalGain(dmicTotalGainDb(dmicGain, adcVol));
        } else {
          setRtkGainValue(data?.realtek_gain ?? 0);
          setRtkVolumeValue(data?.realtek_volume ?? 0);
        }
        setTiGainValue(data?.ti_gain ?? 0);
        setTiVolumeValue(data?.ti_volume ?? 0);
        setGainLoaded(true);
      } catch (err) {
        console.error("Error fetching mic type:", err);
      }
    };

    if (clientId) fetchMicType();
    return () => {
      isCancelled = true;
    };
  }, [clientId, user.token]);

  useEffect(() => {
    //const port = process.env.REACT_APP_WSPORT;
    //const backendUrl = `${window.location.protocol}//${window.location.hostname}:${port}`;
    //const socket = io(backendUrl, {
    //  transports: ['websocket'],
    //  path: '/socket.io'
    //});
    const socketUrtl = process.env.REACT_APP_SOCKET_URL || window.location.origin;
    const socket = io(socketUrtl, { auth: { token: user?.token } });
    socketRef.current = socket;

    // 監聽 socket 數據
    socket.on(`${clientId}-spl`, (data) => {
      const { deviceName, spl } = data;
      if (deviceName === "vpu") {
        // g RMS, often well below 0.01, so keep full precision
        setVpuValue(spl);
        return;
      }
      const formattedSpl = parseFloat(spl.toFixed(2)); // 格式化 SPL 值到小數點第二位
      console.log("Received SPL data:", formattedSpl);
      if (deviceName === "ti") {
        settiSplValue(formattedSpl); // 更新 SPL 數據到狀態
      } else if (deviceName === "realtek") {
        setRtkSplValue(formattedSpl); // 更新 SPL 數據到狀態
      }
    });

    socket.on(`${clientId}-gain`, (data) => {
      const { realtek_gain, ti_gain, realtek_volume, ti_volume } = data;
      console.log("Received Gain data:", data);
      // to parse the gain value
      // {"realtek": 0, "ti": 0}

      // 0 is a valid value (e.g. dmic_gain 0 dB), so only skip missing fields
      if (realtek_gain !== undefined) {
        setRtkGainValue(realtek_gain);
      }
      if (ti_gain !== undefined) {
        setTiGainValue(ti_gain);
      }
      if (realtek_volume !== undefined) {
        setRtkVolumeValue(realtek_volume);
      }
      if (ti_volume !== undefined) {
        setTiVolumeValue(ti_volume);
      }
      if (realtek_gain !== undefined && realtek_volume !== undefined) {
        setAppliedDmicTotalGain(dmicTotalGainDb(realtek_gain, realtek_volume));
      }
    });

    /*
    * to told mqtt_watcher to enable the spl upload
    */
    socket.emit("upload-spl", {
      clientId: clientId,
    });

    // 清理函數
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        console.log("Socket disconnected");
      }
    };
  }, [clientId, user.token]);

  return (
    <>
      <Box sx={{ width: "100%", height: "50%", display: "flex", justifyContent: "center", alignItems: "center"}}>
        {rtkIsDmic ? (
          <VibrationGauge deviceName="Realtek (V2S200D)" gValue={vpuValue} totalGainDb={appliedDmicTotalGain} />
        ) : (
          <SoundLevelGauge deviceName="Realtek" splValue={rtkSplValue}/>
        )}
        <SoundLevelGauge deviceName="TI" splValue={tiSplValue} /> 
      </Box>
      <Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Box sx={{ width: "50%", display: "flex", flexDirection: "column", alignItems: "center" }}>
          {rtkIsDmic ? (
            // Bone conduction sensor: applied immediately by the gain command
            <>
              <h5>Bone conduction sensor, applies immediately</h5>
              <TextField
                select
                label="Realtek Gain (DMIC boost)"
                variant="outlined"
                value={rtkGainValue}
                onChange={(e) => setRtkGainValue(Number(e.target.value))}
                sx={{ width: "200px" }}
              >
                {DMIC_GAIN_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
              <h5>Realtek Volume (ADC): {rtkVolumeValue} ({formatDb(adcVolDb(Number(rtkVolumeValue)))})</h5>
              <Slider
                value={Number(rtkVolumeValue) || 0}
                min={ADC_VOL_MIN}
                max={ADC_VOL_MAX}
                marks={[{ value: ADC_VOL_UNITY, label: "0 dB" }]}
                onChange={(e, value) => setRtkVolumeValue(value)}
                valueLabelDisplay="auto"
                sx={{ width: "200px" }}
              />
              {(() => {
                const totalGain = dmicTotalGainDb(rtkGainValue, Number(rtkVolumeValue));
                return (
                  <Typography variant="body2" color={totalGain >= 24 ? "error" : "text.secondary"} sx={{ mt: 1 }}>
                    Total gain {formatDb(totalGain)}, clips at ≈ {formatG(clipPeakG(totalGain))} peak
                  </Typography>
                );
              })()}
            </>
          ) : (
            <>
              <h5>0: 0 db, 1: 20 db, 2: 30 db, 3: 40 db</h5>
              <TextField
                label="Realtek Gain"
                variant="outlined"
                type="number"
                value={rtkGainValue}
                onChange={(e) => setRtkGainValue(e.target.value)}
                inputProps={{ min: 0, max: 100 }} // 限制輸入範圍
                sx={{ width: "200px" }}
              />
              <h5> 0 - 127 </h5>
              <TextField
                label="Realtek Volume"
                variant="outlined"
                type="number"
                value={rtkVolumeValue}
                onChange={(e) => setRtkVolumeValue(e.target.value)}
                inputProps={{ min: 0, max: 100 }} // 限制輸入範圍
                sx={{ width: "200px" }}
              />
            </>
          )}
        </Box>
        <Box sx={{ width: "50%", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <h5> valure from -42 to 42 </h5>
          <TextField
            label="TI Gain"
            variant="outlined"
            type="number"
            value={tiGainValue}
            onChange={(e) => setTiGainValue(e.target.value)}
            inputProps={{ min: 0, max: 100 }} // 限制輸入範圍
            sx={{ width: "200px" }}
          />
          <h5> 0 - 255 </h5>
          <TextField
            label="TI Volume"
            variant="outlined"
            type="number"
            value={tiVolumeValue}
            onChange={(e) => setTiVolumeValue(e.target.value)}
            inputProps={{ min: 0, max: 100 }} // 限制輸入範圍
            sx={{ width: "200px" }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            TI changes take effect after the device reboots
          </Typography>
        </Box>
      </Box>
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", px: "15%", my: "30px"}}>
        <Button
          variant="contained"
          color="primary"
          disabled={!gainLoaded}
          onClick={() => {
            setTiGainValue(tiGainValue);
            setRtkGainValue(rtkGainValue);
            setTiVolumeValue(tiVolumeValue);
            setRtkVolumeValue(rtkVolumeValue);
            /* send the gain value to the socket */
            if (socketRef.current) {
              console.log("Button clicked");
              socketRef.current.emit("setting", {
                clientId: clientId,
                setting: {
                  realtek_gain: parseInt(rtkGainValue) || 0,
                  ti_gain: parseInt(tiGainValue) || 0,
                  realtek_volume: parseInt(rtkVolumeValue) || 0,
                  ti_volume: parseInt(tiVolumeValue) || 0,
                },
              });
              if (rtkIsDmic) {
                setAppliedDmicTotalGain(dmicTotalGainDb(rtkGainValue, Number(rtkVolumeValue)));
              }
            }
          }}
        >
          Apply
        </Button>
      </Box>
    </>
  );
}

export default SoundLevelGaugeContainer;
